import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { RaceCard } from "@/components/RaceCard";
import { SportIcon } from "@/components/SportIcon";
import api from "@/lib/api";
import { ArrowRight, Search, ChevronLeft, ChevronRight, CheckCircle2, MapPin, Globe } from "lucide-react";

const SPORTS = [
  { slug: "running", name: "Running", img: "https://images.unsplash.com/photo-1452626038306-9aae5e071dd3?crop=entropy&cs=srgb&fm=jpg&q=80&w=600" },
  { slug: "trail-running", name: "Trail Running", img: "https://images.pexels.com/photos/32798746/pexels-photo-32798746.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { slug: "cycling", name: "Cycling", img: "https://images.unsplash.com/photo-1758301059007-0726677b55bb?crop=entropy&cs=srgb&fm=jpg&q=80&w=600" },
  { slug: "triathlon", name: "Triathlon", img: "https://images.unsplash.com/photo-1538992409240-dc1a2ae11e7b?crop=entropy&cs=srgb&fm=jpg&q=80&w=600" },
  { slug: "duathlon", name: "Duathlon", img: "https://images.unsplash.com/photo-1673890704132-9573476ac27c?crop=entropy&cs=srgb&fm=jpg&q=80&w=600" },
  { slug: "fitness-racing", name: "Fitness Racing", img: "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?crop=entropy&cs=srgb&fm=jpg&q=80&w=600" },
];

const LOCATION_FALLBACK = {
  Malaysia: ["Kuala Lumpur", "Selangor", "Penang", "Johor", "Perak", "Kedah", "Kelantan",
    "Terengganu", "Pahang", "Negeri Sembilan", "Melaka", "Sabah", "Sarawak", "Putrajaya", "Labuan", "Perlis"],
  Singapore: [],
};

export default function Home() {
  const [races, setRaces] = useState([]);
  const [states, setStates] = useState(LOCATION_FALLBACK);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/races/upcoming?limit=8").then(({ data }) => setRaces(data.races)).catch(() => {});
    api.get("/races/featured").then(({ data }) => setFeatured(data.races || [])).catch(() => {});
    api.get("/config").then(({ data }) => { if (data.states) setStates(data.states); }).catch(() => {});
  }, []);

  return (
    <Layout>
      {/* HERO */}
      <section className="relative bg-[#0a1420] overflow-hidden">
        <img src="https://images.pexels.com/photos/29840338/pexels-photo-29840338.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
          alt="Runners" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 hero-overlay-mobile sm:hidden" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-32">
          <div className="max-w-2xl animate-fade-up">
            <span className="font-display font-semibold text-[#22d3ee] tracking-[0.25em] text-xs sm:text-sm uppercase">Malaysia &amp; Singapore Race Calendar</span>
            <h1 className="font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.9] mt-4">
              Find Your<br /><span className="text-[#22d3ee]">Next Race</span>
            </h1>
            <p className="text-slate-200 text-base sm:text-lg mt-5 max-w-lg">Discover running, trail, cycling, triathlon, duathlon and fitness racing events across Malaysia &amp; Singapore.</p>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 max-w-lg rounded-lg bg-white/10 border border-white/20 backdrop-blur-sm px-4 py-3" data-testid="hero-runner-signup">
              <div className="min-w-0">
                <p className="font-display font-bold uppercase tracking-wide text-[#22d3ee] text-sm">Runners — Sign Up Free</p>
                <p className="text-slate-200 text-xs sm:text-sm mt-0.5">Save favourites • Build your race calendar • Share races with friends</p>
              </div>
              <Link to="/register" data-testid="hero-runner-signup-btn" className="shrink-0 inline-flex items-center gap-1.5 self-start sm:self-auto bg-white/15 border border-white/30 text-white font-semibold text-sm px-4 py-2 rounded-lg hover:bg-white/25 transition-colors">Sign Up as a Runner <ArrowRight size={15} /></Link>
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/races" data-testid="hero-find-race" className="inline-flex items-center gap-2 bg-[#22d3ee] text-[#0a1420] font-bold px-6 py-3.5 rounded-lg hover:bg-white transition-colors">Find a Race <ArrowRight size={18} /></Link>
              <Link to="/for-organizers" data-testid="hero-list-race" className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white font-bold px-6 py-3.5 rounded-lg hover:bg-white/20 transition-colors backdrop-blur-sm">List Your Race</Link>
            </div>
          </div>
        </div>
      </section>

      {/* SPORT STRIP */}
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-6 sm:gap-10 overflow-x-auto no-scrollbar py-4">
          {SPORTS.map((s) => (
            <Link key={s.slug} to={`/races?sport=${s.slug}`} className="flex items-center gap-2 whitespace-nowrap text-slate-700 hover:text-[#06b6d4] font-semibold text-sm" data-testid={`sport-strip-${s.slug}`}>
              <SportIcon sport={s.slug} size={18} />{s.name}
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED / UPCOMING RACES CAROUSEL (white) */}
      {(races.length > 0 || featured.length > 0) && <RaceCarousel races={featured.length > 0 ? featured : races} />}


      {/* BROWSE BY SPORT */}
      <section className="bg-slate-100 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-display font-extrabold uppercase tracking-wide text-3xl sm:text-4xl text-[#0f172a]">Browse by Sport</h2>
            <Link to="/races" className="inline-flex items-center gap-1 text-[#06b6d4] font-semibold text-sm hover:underline">View All <ArrowRight size={16} /></Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {SPORTS.map((s) => (
              <Link key={s.slug} to={`/races?sport=${s.slug}`} data-testid={`browse-sport-${s.slug}`}
                className="group relative h-32 rounded-xl overflow-hidden">
                <img src={s.img} alt={s.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-[#0a1420]/60 group-hover:bg-[#0a1420]/40 transition-colors" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white gap-1.5">
                  <SportIcon sport={s.slug} size={24} className="text-[#22d3ee]" />
                  <span className="font-display font-bold uppercase text-sm tracking-wide text-center px-1">{s.name}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BROWSE BY LOCATION */}
      <section className="bg-[#0a1420] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="font-display font-semibold text-[#22d3ee] tracking-[0.25em] text-xs uppercase">Where to race</span>
              <h2 className="font-display font-extrabold uppercase tracking-wide text-3xl sm:text-4xl text-white mt-2">Browse by Location</h2>
            </div>
            <Link to="/races" className="hidden sm:inline-flex items-center gap-1 text-[#22d3ee] font-semibold text-sm hover:underline">View All <ArrowRight size={16} /></Link>
          </div>
          <div className="space-y-8">
            {Object.entries(states).map(([country, list]) => (
              <div key={country}>
                <div className="flex items-center gap-3 mb-4">
                  <Globe size={18} className="text-[#22d3ee]" />
                  <h3 className="font-display font-bold uppercase tracking-wide text-white text-lg">{country}</h3>
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {(list && list.length ? list : [country]).map((name) => {
                    const to = (list && list.length)
                      ? `/races?country=${encodeURIComponent(country)}&state=${encodeURIComponent(name)}`
                      : `/races?country=${encodeURIComponent(country)}`;
                    return (
                      <Link key={name} to={to} data-testid={`browse-location-${name}`}
                        className="group inline-flex items-center gap-2 bg-white/5 hover:bg-[#22d3ee] border border-white/10 hover:border-[#22d3ee] text-slate-200 hover:text-[#0a1420] rounded-full pl-3 pr-4 py-2 transition-colors duration-200">
                        <MapPin size={14} className="text-[#22d3ee] group-hover:text-[#0a1420] transition-colors" />
                        <span className="font-semibold text-sm">{name}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR ORGANIZERS BANNER */}
      <section className="bg-[#0a1420]">
        <Link to="/organizer/register" data-testid="organizers-cta" className="block w-full hover:opacity-95 transition-opacity">
          <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_finder-events-my/artifacts/quub1ehb_ChatGPT%20Image%20Sep%2030%2C%202026%2C%2003_58_01%20PM.webp" alt="List Your Race — More Visibility. More Runners. Reach thousands of runners looking for their next event." className="w-full h-auto block" />
        </Link>
      </section>
    </Layout>
  );
}

function RaceCarousel({ races }) {
  const PER = 4;
  const pages = Math.ceil(races.length / PER);
  const [page, setPage] = useState(0);
  const autoRotate = pages > 1;

  useEffect(() => { if (page >= pages) setPage(0); }, [pages, page]);
  useEffect(() => {
    if (!autoRotate) return;
    const t = setInterval(() => setPage((p) => (p + 1) % pages), 7000);
    return () => clearInterval(t);
  }, [autoRotate, pages]);

  const go = (dir) => setPage((p) => (p + dir + pages) % pages);
  const current = races.slice(page * PER, page * PER + PER);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="featured-strip">
      <div className="flex items-end justify-between mb-6 gap-4">
        <div>
          <h2 className="font-display font-extrabold uppercase tracking-wide text-3xl sm:text-4xl text-[#0f172a]">Upcoming Races</h2>
          <p className="text-slate-500 mt-1 text-sm">Featured events from across Malaysia &amp; Singapore</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/races" className="hidden sm:inline-flex items-center gap-1 text-[#06b6d4] font-semibold text-sm hover:underline mr-2">View All Races <ArrowRight size={16} /></Link>
          {pages > 1 && <>
            <button onClick={() => go(-1)} data-testid="featured-prev" aria-label="Previous races" className="p-2 border border-slate-300 rounded-full hover:bg-slate-100 transition-colors"><ChevronLeft size={18} /></button>
            <button onClick={() => go(1)} data-testid="featured-next" aria-label="Next races" className="p-2 border border-slate-300 rounded-full hover:bg-slate-100 transition-colors"><ChevronRight size={18} /></button>
          </>}
        </div>
      </div>
      <div key={page} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-up" data-testid="featured-page">
        {current.map((r) => <RaceCard key={r.id} race={r} />)}
      </div>
      {pages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: pages }).map((_, i) => (
            <button key={i} onClick={() => setPage(i)} aria-label={`Go to page ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === page ? "w-6 bg-[#06b6d4]" : "w-2 bg-slate-300 hover:bg-slate-400"}`} />
          ))}
        </div>
      )}
    </section>
  );
}
